import React from "react";
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";

import { Partner, PartnerResource } from "../../../../api/partners";

export interface PartnerAssociationHandle {
  /** Persist the current selection. Throws on failure (leaves an error toast). */
  save: () => Promise<void>;
  /** Throws (with an error toast) when the selection is incomplete — called by
   * the parent BEFORE it persists anything, so a bad partner selection can't
   * leave a partially-applied dialog save. */
  validate: () => void;
  /**
   * The partner this meeting is about to be private to, or null when it is
   * being saved public. The parent needs it to check the continuous room key
   * against other meetings before persisting — that check spans the key field
   * (which lives in the dialog) and this selection, so neither side can make
   * it alone.
   */
  desiredPartnerId: () => string | null;
  /** Surfaces a blocking message from the parent's own validation. */
  reportError: (message: string) => void;
}

interface Props {
  gcalEventId: string;
  /** Recurring-series master id, when this instance belongs to a series. */
  recurringMeetingId?: string;
  /**
   * The scope as AUTHORED on the calendar event (metadata.partnerId), not as
   * projected into DynamoDB. Those differ for the minutes between a save and
   * the next sync pass, and showing the projection would make a just-saved
   * change look like it did not take.
   */
  partnerId?: string;
}

/**
 * Marks a meeting as ONE partner's "Partner-only" (private, partner-scoped)
 * OR as "Preferred" (differentiated card, visual only) for ONE OR MORE
 * partners — mutually exclusive modes. Partner-only writes Meeting.partnerId
 * (one-to-one); Preferred writes the stable session key into EACH selected
 * partner's preferredSessionIds (one-to-many: every partner's members see
 * their own partner's styling, unaware of the others). Persistence is driven
 * by the parent dialog's Save (via the imperative `save()` handle).
 */
export const PartnerAssociationSection = React.forwardRef<
  PartnerAssociationHandle,
  Props
>(({ gcalEventId, recurringMeetingId, partnerId }, ref) => {
  // A recurring series is one preferred key (its master id); a one-off is its
  // own gcal event id.
  const stableKey = recurringMeetingId ?? gcalEventId;

  const [loading, setLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState(false);
  const [partners, setPartners] = React.useState<Partner[]>([]);
  const [partnerOnly, setPartnerOnly] = React.useState(false);
  const [privatePartnerId, setPrivatePartnerId] = React.useState<string>("");
  const [preferred, setPreferred] = React.useState(false);
  const [preferredPartnerIds, setPreferredPartnerIds] = React.useState<
    string[]
  >([]);
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  // Section state as loaded. The parent dialog calls save() unconditionally on
  // every Save, so an untouched section MUST be a no-op — otherwise merely
  // saving the dialog would rewrite partner lists (e.g. wipe another partner's
  // preferred mark on a legacy private+preferred meeting).
  const initialState = React.useRef<{
    partnerOnly: boolean;
    privatePartnerId: string;
    preferred: boolean;
    preferredPartnerIds: string[];
  } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError(false);

    // Only the partner list is fetched now — the scope arrives as a prop,
    // read from the calendar metadata the admin authored.
    const pid = partnerId?.trim() || null;
    PartnerResource.getListCached()
      .catch(() => [] as Partner[])
      .then((ps) => {
        if (cancelled) return;
        setPartners(ps);
        // Preferred is one-to-many — collect EVERY partner that already lists
        // this session's key.
        const ownerIds = ps
          .filter((p) => p.preferredSessionIds?.includes(stableKey))
          .map((p) => p.id);
        setPrivatePartnerId(pid ?? "");
        setPartnerOnly(!!pid);
        setPreferredPartnerIds(ownerIds);
        setPreferred(!pid && ownerIds.length > 0);
        initialState.current = {
          partnerOnly: !!pid,
          privatePartnerId: pid ?? "",
          preferred: !pid && ownerIds.length > 0,
          preferredPartnerIds: ownerIds,
        };
      })
      .catch(() => {
        if (!cancelled) setFetchError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [partnerId, stableKey]);

  // Mutually exclusive: enabling one disables the other.
  const handlePartnerOnly = (checked: boolean) => {
    setPartnerOnly(checked);
    if (checked) setPreferred(false);
  };
  const handlePreferred = (checked: boolean) => {
    setPreferred(checked);
    if (checked) setPartnerOnly(false);
  };

  const sameIdSet = (a: string[], b: string[]) =>
    a.length === b.length && a.every((id) => b.includes(id));

  // Untouched (or never-loaded) section — the parent dialog saves
  // unconditionally, so that MUST write nothing.
  const isDirty = () => {
    const initial = initialState.current;
    return (
      !!initial &&
      (partnerOnly !== initial.partnerOnly ||
        privatePartnerId !== initial.privatePartnerId ||
        preferred !== initial.preferred ||
        !sameIdSet(preferredPartnerIds, initial.preferredPartnerIds))
    );
  };

  const validate = () => {
    if (!isDirty()) return;
    if (partnerOnly && !privatePartnerId) {
      setSnackbar({ open: true, message: "Select a partner first" });
      throw new Error("No partner selected");
    }
    if (preferred && preferredPartnerIds.length === 0) {
      setSnackbar({ open: true, message: "Select at least one partner" });
      throw new Error("No partner selected");
    }
  };

  const persist = async () => {
    if (!isDirty()) return;
    validate();
    try {
      // Partner scope is NOT written here. It rides the parent dialog's
      // metadata write (see InstanceMetadataEditDialog), because the calendar
      // event is its source of truth: the sync stamps it onto every occurrence
      // of a series, including ones that do not exist yet. Writing it straight
      // to DynamoDB from here would be reverted on the next sync pass.
      const desiredPartnerId = partnerOnly ? privatePartnerId : null;

      // Preferred lists: diff EVERY partner against the desired selection so
      // the key is added to newly-selected partners and removed from
      // deselected ones (and from all of them when private/none is chosen).
      // Diff against a FRESH list (the dialog's copy may be minutes old) and
      // send add/remove INTENTS — the server applies them to its own stored
      // list, so no stale snapshot ever replaces another admin's edits.
      const fresh = await PartnerResource.getList().catch(() => partners);
      const desired = new Set(preferred ? preferredPartnerIds : []);
      const changed = fresh.filter((p) => {
        const has = p.preferredSessionIds?.includes(stableKey) ?? false;
        return has !== desired.has(p.id);
      });
      const updated = await Promise.all(
        changed.map((p) =>
          PartnerResource.update(
            p.id,
            desired.has(p.id)
              ? { addPreferredSessionIds: [stableKey] }
              : { removePreferredSessionIds: [stableKey] },
          ),
        ),
      );

      // Keep the local copies in sync so a second save diffs correctly.
      if (updated.length) {
        setPartners((prev) =>
          prev.map((p) => updated.find((u) => u.id === p.id) ?? p),
        );
      }
      initialState.current = {
        partnerOnly,
        privatePartnerId,
        preferred,
        preferredPartnerIds,
      };
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to save partner settings" });
      throw error;
    }
  };

  // Persistence is triggered by the parent dialog's Save (single save button).
  React.useImperativeHandle(ref, () => ({
    save: persist,
    validate,
    desiredPartnerId: () => (partnerOnly ? privatePartnerId || null : null),
    reportError: (message: string) => setSnackbar({ open: true, message }),
  }));

  const nameOf = (id: string) =>
    partners.find((p) => p.id === id)?.displayName ?? id;

  if (fetchError) {
    return (
      <>
        <Divider sx={{ my: 1 }} />
        <Alert severity="warning" sx={{ mt: 1 }}>
          Could not load partner settings. Check your connection and try again.
        </Alert>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Divider sx={{ my: 1 }} />
        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Skeleton variant="text" width={160} height={24} />
          <Skeleton variant="rounded" height={40} sx={{ mt: 1.5 }} />
          <Skeleton variant="text" width={200} height={32} sx={{ mt: 1 }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Box
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Partner Association
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={partnerOnly}
              onChange={(e) => handlePartnerOnly(e.target.checked)}
            />
          }
          label="Partner-only (private)"
        />
        <FormHelperText sx={{ mt: -0.5, mb: 1 }}>
          Only one partner&apos;s members (plus Admins/Hosts) can see and join.
          Mutually exclusive with Preferred.
        </FormHelperText>
        {partnerOnly && (
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel id="partner-private-label">Partner</InputLabel>
            <Select
              labelId="partner-private-label"
              label="Partner"
              value={privatePartnerId}
              onChange={(e) => setPrivatePartnerId(e.target.value)}
            >
              {partners.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={preferred}
              onChange={(e) => handlePreferred(e.target.checked)}
            />
          }
          label="Preferred"
        />
        <FormHelperText sx={{ mt: -0.5, mb: 1 }}>
          Shows a differentiated card to each selected partner&apos;s members
          (visual only — anyone can still join). Multiple partners can prefer
          the same session; each member sees their own partner&apos;s styling.
          Saved with the dialog&apos;s Save button.
        </FormHelperText>
        {preferred && (
          <FormControl fullWidth size="small">
            <InputLabel id="partner-preferred-label">Partners</InputLabel>
            <Select
              labelId="partner-preferred-label"
              label="Partners"
              multiple
              value={preferredPartnerIds}
              onChange={(e) => {
                const value = e.target.value;
                setPreferredPartnerIds(
                  typeof value === "string" ? value.split(",") : value,
                );
              }}
              renderValue={(selected) => selected.map(nameOf).join(", ")}
            >
              {partners.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <Checkbox
                    size="small"
                    checked={preferredPartnerIds.includes(p.id)}
                  />
                  <ListItemText primary={p.displayName} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});

PartnerAssociationSection.displayName = "PartnerAssociationSection";
